import type { UseUploadFileResult } from '@/hooks/useUploadFile'
import { filesApi, type FileDoc } from '@/services/api/domains/files.api'

import { FILE_TYPE_TO_JOB_TYPE, type ImportFileType } from './import-file'

export interface UploadLibraryFileArgs {
  file: File
  fileType: ImportFileType
  title: string
  description?: string
}

/**
 * Puts a file in the Library: the S3 multipart upload (`useUploadFile`'s `start`), then the File record
 * that points at it. Shared by the Import dialog and the assistant's attachment picker, so a file the
 * assistant receives lands exactly as one imported by hand. Publishing is the caller's next step, not this.
 *
 * `signal`, once aborted, stops it before the File record is created — even when the upload itself had
 * already finished — so a caller that gave up (another user signed in, the chip was removed) never
 * creates a Library item with whatever token is current by then.
 */
export async function uploadLibraryFile(
  upload: Pick<UseUploadFileResult, 'start'>,
  { file, fileType, title, description }: UploadLibraryFileArgs,
  signal?: AbortSignal,
): Promise<FileDoc> {
  const { s3Bucket, s3Key } = await upload.start({ file, type: FILE_TYPE_TO_JOB_TYPE[fileType], input: {} })

  if (signal?.aborted) throw new DOMException('Upload cancelled', 'AbortError')

  const fileDoc = await filesApi.create({
    title,
    description,
    fileName: file.name,
    fileType,
    fileSize: file.size,
    s3Bucket,
    s3Key,
  })

  return fileDoc.data
}
